import { useEffect, useRef, useState } from "react";
import { AntiMetalButton } from "../components/ui/anti-metal-button";
import { HandwritingText } from "../components/ui/handwriting-text";
import "./IntelligenceHero.css";

/* ── Animated counter ── */
function useCounter(target, decimals = 0) {
  const [val, setVal] = useState(0);
  const triggered = useRef(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        const steps = 70, dur = 1800;
        let i = 0;
        const tick = setInterval(() => {
          i++;
          const p = 1 - Math.pow(1 - i / steps, 3);
          const v = target * p;
          setVal(decimals > 0 ? parseFloat(v.toFixed(decimals)) : Math.floor(v));
          if (i >= steps) { clearInterval(tick); setVal(target); }
        }, dur / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, decimals]);
  return [val, ref];
}

function StatCard({ prefix = "", target, suffix = "", decimals = 0, label, desc, bg, textColor, rotate = 0, index = 0 }) {
  const [val, ref] = useCounter(target, decimals);
  const display = decimals > 0 ? val.toFixed(decimals) : val;
  return (
    <div
      ref={ref}
      className="ih-scard"
      style={{ background: bg, color: textColor, transform: `rotate(${rotate}deg)`, animationDelay: `${0.7 + index * 0.08}s` }}
    >
      <div className="ih-scard-num">{prefix}{display}{suffix}</div>
      <div className="ih-scard-label">{label}</div>
      <div className="ih-scard-rule" style={{ background: textColor === '#fff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)' }} />
      <div className="ih-scard-desc">{desc}</div>
    </div>
  );
}

export default function IntelligenceHero() {
  return (
    <section className="ih-section">
      <div className="ih-inner">

        {/* Eyebrow */}
        <div className="ih-eyebrow-wrap">
          <span className="ih-eyebrow-dot" />
          <span>Aurelix Intelligence</span>
        </div>

        {/* Headline */}
        <h1 className="ih-heading">
          More data doesn't automatically mean{" "}
          <HandwritingText
            words={[
              "better decisions.",
              "clearer direction.",
              "smarter strategy.",
              "real insight.",
            ]}
            interval={3200}
            duration={1.5}
            height="1.1em"
            className="ih-hw-word"
          />
        </h1>

        {/* Subtext */}
        <p className="ih-subline">
          Most businesses already have dashboards. The problem is often that no one knows which number matters — or what to do when it changes. Aurelix Intelligence turns fragmented information into a clearer view of what is happening across your business.
        </p>

        {/* CTA row */}
        <div className="ih-cta-row">
          <AntiMetalButton
            as="a"
            href="#intelligence-services"
            accentFrom="#3b82f6"
            accentTo="#2563eb"
          >
            See How It Works
          </AntiMetalButton>
          <a href="#intelligence-contact" className="ih-link">
            Talk to the team
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        {/* Stat cards */}
        <div className="ih-scards-wrap">
          <StatCard
            prefix="" target={20} suffix="+"
            decimals={0} label="Brands Tracked"
            desc="Active reporting across D2C, SaaS and services"
            bg="#3b82f6" textColor="#fff"
            rotate={-4} index={0}
          />
          <StatCard
            prefix="" target={100} suffix="+"
            decimals={0} label="Dashboards Built"
            desc="Custom intelligence systems for real decisions"
            bg="#f5c024" textColor="#0d0d0d"
            rotate={0} index={1}
          />
          <StatCard
            prefix="" target={95} suffix="%"
            decimals={0} label="Client Retention"
            desc="Because clarity keeps teams coming back"
            bg="#1a1a1a" textColor="#fff"
            rotate={4} index={2}
          />
        </div>

      </div>
    </section>
  );
}
